import { useEffect, useState } from 'react';
import { trackShare } from '../core/analytics/google-analytics';
import type { ShareChannel } from '../core/analytics/share-channel';
import { messengerShareHref, sharedPageUrl } from '../core/analytics/share-link';
import { useHydrated } from '../core/hooks/use-hydrated';
import { useTranslation } from '../core/hooks/use-translation';
import { CheckIcon, LinkIcon, SendIcon, ShareIcon } from './portal-icons';

interface ShareBarProps {
    /** The page's canonical path, `/kk/blog/<slug>` — never the address bar, which may carry tags. */
    path: string;
    title: string;
    /** `utm_campaign`, and GA's `content_type` for the share event. */
    campaign: string;
    /** `utm_content`, and GA's `item_id`: which page within the campaign was shared. */
    content: string;
}

const BUTTON =
    'inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-brand-700 dark:hover:text-brand-400';

/**
 * WhatsApp, Telegram, the device's share sheet and a copy button — each handing out a link tagged
 * with where it went, so GA can credit the share that brought a visitor back.
 *
 * **Nothing renders until hydration.** A messenger needs an absolute URL and the build has no origin
 * to put in one, and whether the device has a share sheet at all is only known in the browser.
 * Drawing the buttons at build time would mean links that point nowhere until React replaced them.
 */
export function ShareBar({ path, title, campaign, content }: ShareBarProps) {
    const { t } = useTranslation();
    const hydrated = useHydrated();
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) {
            return;
        }

        const timer = window.setTimeout(() => setCopied(false), 2000);

        return () => window.clearTimeout(timer);
    }, [copied]);

    if (!hydrated) {
        return null;
    }

    const pageUrl = new URL(path, window.location.origin).toString();
    const tagged = (channel: ShareChannel) => sharedPageUrl(pageUrl, channel, campaign, content);
    const canShareNatively = typeof navigator.share === 'function';

    const shareNatively = async () => {
        try {
            await navigator.share({ title, url: tagged('native') });
            trackShare('native', campaign, content);
        } catch {
            // Dismissing the sheet rejects too, and that is not a failure worth showing anyone.
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(tagged('copy'));
            setCopied(true);
            trackShare('copy', campaign, content);
        } catch {
            // Clipboard refused, usually an insecure origin or a denied permission. The address bar
            // still holds the page, untagged, which is the fallback a visitor reaches for anyway.
        }
    };

    return (
        <section className="mt-10 flex flex-wrap items-center gap-2" aria-label={t('share.title')}>
            <span className="mr-1 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('share.title')}</span>

            <a
                href={messengerShareHref('whatsapp', tagged('whatsapp'), title)}
                target="_blank"
                rel="noreferrer noopener"
                className={BUTTON}
                onClick={() => trackShare('whatsapp', campaign, content)}
            >
                <img src="/media/icons/whatsapp.svg" alt="" className="size-4" />
                WhatsApp
            </a>

            <a
                href={messengerShareHref('telegram', tagged('telegram'), title)}
                target="_blank"
                rel="noreferrer noopener"
                className={BUTTON}
                onClick={() => trackShare('telegram', campaign, content)}
            >
                <SendIcon className="size-4" />
                Telegram
            </a>

            {canShareNatively && (
                <button type="button" className={BUTTON} onClick={() => void shareNatively()}>
                    <ShareIcon className="size-4" />
                    {t('share.native')}
                </button>
            )}

            <button type="button" className={BUTTON} onClick={() => void copy()} aria-live="polite">
                {copied ? <CheckIcon className="size-4" /> : <LinkIcon className="size-4" />}
                {t(copied ? 'share.copied' : 'share.copy')}
            </button>
        </section>
    );
}
